import { type useAuth0 } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';

import { PAYER_KEY } from './constants';
import { handleError } from './Error';
import { AppRoutes } from './routes';
import { SocketTypes, wsEnsureConnected } from './socketHelpers';

import { fmhAPI } from 'api/APIBase';
import { getFmhToken } from 'api/authAPI';
import { type AppDispatch } from 'store';
import { setAccessToken } from 'store/auth/authSlice';

export const retrieveAndSetFmhToken = async ({
    getAccessTokenSilently,
    auth0AccessToken,
    dispatch,
    payerId = undefined,
}: {
    getAccessTokenSilently?: ReturnType<typeof useAuth0>['getAccessTokenSilently'];
    auth0AccessToken?: string;
    dispatch: AppDispatch;
    payerId?: string;
}) => {
    const token = auth0AccessToken ?? (await getAccessTokenSilently?.());
    const currentPayerId = sessionStorage.getItem(PAYER_KEY);
    const fmhTokenResult = await getFmhToken({
        accessToken: token!,
        payerId: payerId ?? currentPayerId,
    });

    const fmhAccessToken = fmhTokenResult.access_token;
    fmhAPI.base = fmhAPI.base.auth(`Bearer ${fmhAccessToken}`);

    dispatch(
        setAccessToken({
            accessToken: fmhAccessToken,
            payerId: fmhTokenResult.npi,
            segmentIds: fmhTokenResult.segment_ids,
            permissions: fmhTokenResult.permissions,
            restrictedAccess: fmhTokenResult.restricted_access,
            analyticsAccess: fmhTokenResult.analytics_access,
        }),
    );
    if (payerId) {
        sessionStorage.setItem(PAYER_KEY, payerId);
    }
    return fmhTokenResult;
};

export const clearDataForLogout = (clearSessionStorage = true) => {
    if (clearSessionStorage) {
        sessionStorage.clear();
    }
    datadogRum.clearUser();
    pendo.clearSession?.(); // Optional chaining to avoid error if pendo is not initialized
};

export const getFMHTokenAndEnsureSocketConnections = async ({
    getAccessTokenSilently,
    auth0AccessToken,
    logout,
    dispatch,
    payerId = undefined,
}: {
    getAccessTokenSilently?: ReturnType<typeof useAuth0>['getAccessTokenSilently'];
    auth0AccessToken?: string;
    logout: ReturnType<typeof useAuth0>['logout'];
    dispatch: AppDispatch;
    payerId?: string;
}) => {
    try {
        const fmhTokenResult = await retrieveAndSetFmhToken({
            getAccessTokenSilently,
            auth0AccessToken,
            dispatch,
            payerId,
        });

        dispatch(wsEnsureConnected({ socketType: SocketTypes.NOTIFICATION }));
        return fmhTokenResult;
    } catch (err) {
        handleError('Error authenticating.', err as Error);
        clearDataForLogout();
        await logout({
            logoutParams: {
                returnTo: `${window.location.origin}${AppRoutes.loggedOut}`,
            },
        });
        return undefined;
    }
};
